import type { SmartSortProduct } from '@iheartjane/dm-sdk';
import { Button, CheckIcon } from '@iheartjane/reefer';
import type { SyntheticEvent } from 'react';
import { Form } from 'react-router';

import type { AddToCartFormArgs } from '@jane/bloom/hooks/useAddToCartConfig';
import { useAddToCartConfig } from '@jane/bloom/hooks/useAddToCartConfig';
import { useOptimisticBagSize } from '@jane/bloom/hooks/useBagSize';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { getMaxCartQuantity } from '@jane/bloom/utils/getMaxCartQuantity';
import type { ProductSearchResultViewModel } from '@jane/bloom/viewModels/productSearch';
import type { AlgoliaProduct } from '@jane/search/types';

import { AddToCartLabel } from '../productCard/components/addToCartLabel';

import styles from './wizardAddToCartButton.module.css';

interface WizardAddToCartButtonProps {
  checkAndHandleTransfer: (item: {
    count: number;
    priceId?: string;
    productId: number;
  }) => boolean;
  index: number;
  onTransferTriggered: (formArgs: AddToCartFormArgs) => void;
  product: ProductSearchResultViewModel;
  smartProductInstance: SmartSortProduct<AlgoliaProduct>;
}

export const WizardAddToCartButton = ({
  checkAndHandleTransfer,
  index,
  onTransferTriggered,
  product,
  smartProductInstance,
}: WizardAddToCartButtonProps) => {
  const { cart, shoppingPreferences } = useRootLoaderData();
  const selectedWeight = product.available_weights?.[0] ?? 'each';

  const {
    fetcher: { state },
    formArgs,
  } = useAddToCartConfig({
    cardLocation: 'wizard',
    index,
    product,
    productId: product.product_id,
    quantity: 1,
    selectedWeight,
  });

  const countInCart = useOptimisticBagSize(product.product_id);
  const isAddingToCart = state === 'submitting';
  const atMaxQuantity =
    getMaxCartQuantity({
      cart,
      currentProduct: product,
      currentWeight: selectedWeight,
      storeId: shoppingPreferences?.storeId,
    }) === 0;

  const onSubmit = (event: SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
    smartProductInstance.click({ isAddToCart: true });

    const needsTransfer = checkAndHandleTransfer({
      count: 1,
      priceId: selectedWeight,
      productId: product.product_id,
    });

    if (needsTransfer) {
      event.preventDefault();
      onTransferTriggered(formArgs);
    }
  };

  return (
    <Form
      name={`wizard-${product.product_id}-add-to-cart`}
      onSubmit={onSubmit}
      {...formArgs}
    >
      <input type="hidden" name="productId" value={product.product_id} />
      <input type="hidden" name="priceId" value={selectedWeight} />
      <input type="hidden" name="count" value={1} />

      <Button
        disabled={isAddingToCart || atMaxQuantity}
        full
        label={
          <AddToCartLabel
            atMaxQuantity={atMaxQuantity}
            countInCart={countInCart}
            hasMultipleWeights={false}
            isBeingAddedToCart={isAddingToCart}
          />
        }
        startIcon={
          isAddingToCart && (
            <CheckIcon className={styles['textContrastColor']} />
          )
        }
        type="submit"
        variant="primary"
      />
    </Form>
  );
};
